import React, { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, ChevronDown, Loader2, ScanLine, X, ZoomIn } from "lucide-react";
import { getPaydJob, getPaydJobs, paydImageUrl } from "./paydApi";
import { useInfiniteScrollSentinel } from "../hooks/useInfiniteScrollSentinel";

/**
 * PAYD — three readings per job, taken off three photographs.
 *
 * The list is the readings; the popup is the photographs behind them, because
 * the only question a reader has about a reading is "does the picture agree".
 * There is little else in the record, which is why this is a dialog rather
 * than a page.
 */

const card = "rounded-card border border-cl-card bg-cl-surface";
const cell = "px-3 py-[9px] align-middle";
// z-[1], not the z-10 utility: both the date picker and status dropdown
// below use the z-dropdown token, which this project's own scale
// (design-tokens.css) sets to 10 — identical to the plain Tailwind z-10 this
// used to borrow, which is how an open popover ended up rendering BEHIND
// this header instead of over it. This only needs to clear the unstyled
// (z-index: auto) rows scrolling underneath it, not compete with a
// page-level dropdown for the top layer.
const headCell =
  "sticky top-0 z-[1] bg-cl-head px-3 py-[8px] text-left text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label";

const FILL = "#2a78d6";
const num = (value) => (value || 0).toLocaleString("en-GB");

const iso = (date) => date.toISOString().slice(0, 10);
const RANGE_DAYS = 30;

// review_status's four real values, confirmed against a captured request for
// each one — the dashboard's own dropdown sends the number, not the label,
// so this is the only place that vocabulary is written down. 0 is "all
// statuses", not "no filter sent" — the backend expects it explicitly
// (confirmed by a captured request pairing status=0 with a date range).
const STATUS_OPTIONS = [
  { value: "0", label: "All statuses" },
  { value: "1", label: "Completed" },
  { value: "2", label: "Under Review" },
  { value: "3", label: "Review Completed" },
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const stamp = (value) => {
  if (typeof value !== "string" || value.length < 10) return "—";
  // Some backends send "YYYY-MM-DDTHH:MM:SS" instead of the space-separated
  // shape this was written for — normalize before splitting, same fix as
  // peopleList.js's formatModified, or the day and time both silently drop.
  const [date, time = ""] = value.replace("T", " ").split(" ");
  const [year, month, day] = date.split(/[-/]/);
  const name = MONTHS[Number(month) - 1];
  if (!name) return value;
  return `${Number(day)} ${name} ${year}${time ? `, ${time.slice(0, 5)}` : ""}`;
};

/** A reading, or a visible statement that there was not one. */
function Reading({ value }) {
  if (!value) return <span className="text-cl-faint">not read</span>;
  return <span className="font-medium text-cl-text">{value}</span>;
}

const ZOOM_LENS_SIZE = 130;
const ZOOM_FACTOR = 2.5;

/**
 * A hover magnifier, the same affordance the old PAYD portal's own case
 * dialog has — a circular lens that follows the cursor, showing a zoomed
 * crop under it, so a reviewer can check a reading against the photograph
 * without leaving the dialog.
 *
 * The thumbnail is object-cover, which crops rather than stretches the
 * photo to fill its box — the lens has to reproduce that exact crop, scaled
 * up, or it zooms into a part of the image the thumbnail itself never
 * showed. That needs the photo's own natural dimensions (object-cover's
 * crop depends on how its aspect ratio compares to the box's), read off the
 * loaded <img> the same way ImageLightbox.js tracks naturalDimensions for
 * its own small-image zoom.
 */
function ZoomImage({ src, alt }) {
  const containerRef = useRef(null);
  const [natural, setNatural] = useState(null);
  const [cursor, setCursor] = useState(null);

  const handleMouseMove = (event) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCursor({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
    });
  };

  const lensStyle = (() => {
    if (!cursor || !natural || !natural.width || !natural.height) return null;

    // The same scale object-fit: cover applies — the image is scaled up
    // until it fills the box in both dimensions, cropping whichever
    // dimension overflows, centered.
    const coverScale = Math.max(
      cursor.width / natural.width,
      cursor.height / natural.height,
    );
    const scaledWidth = natural.width * coverScale;
    const scaledHeight = natural.height * coverScale;
    const cropX = (scaledWidth - cursor.width) / 2;
    const cropY = (scaledHeight - cursor.height) / 2;

    // Where the cursor actually points, in the cover-scaled image.
    const pointX = cropX + cursor.x;
    const pointY = cropY + cursor.y;

    return {
      left: cursor.x - ZOOM_LENS_SIZE / 2,
      top: cursor.y - ZOOM_LENS_SIZE / 2,
      width: ZOOM_LENS_SIZE,
      height: ZOOM_LENS_SIZE,
      backgroundImage: `url(${src})`,
      backgroundRepeat: "no-repeat",
      backgroundSize: `${scaledWidth * ZOOM_FACTOR}px ${scaledHeight * ZOOM_FACTOR}px`,
      backgroundPosition: `${-(pointX * ZOOM_FACTOR - ZOOM_LENS_SIZE / 2)}px ${-(
        pointY * ZOOM_FACTOR - ZOOM_LENS_SIZE / 2
      )}px`,
    };
  })();

  return (
    <div
      ref={containerRef}
      role="button"
      tabIndex={0}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setCursor(null)}
      onClick={() => window.open(src, "_blank", "noopener,noreferrer")}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          window.open(src, "_blank", "noopener,noreferrer");
        }
      }}
      className="relative mt-2 block cursor-zoom-in overflow-hidden rounded-tile border border-cl-card"
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="block h-[150px] w-full bg-cl-bg object-cover"
        onLoad={(event) =>
          setNatural({
            width: event.target.naturalWidth,
            height: event.target.naturalHeight,
          })
        }
      />
      {!cursor && (
        <span className="pointer-events-none absolute bottom-[6px] right-[6px] flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[rgba(19,23,32,0.55)] text-white">
          <ZoomIn className="h-[12px] w-[12px]" />
        </span>
      )}
      {lensStyle && (
        <div
          className="pointer-events-none absolute rounded-full border-2 border-white"
          style={{ ...lensStyle, boxShadow: "0 2px 14px rgba(0,0,0,0.4)" }}
        />
      )}
    </div>
  );
}

function ReadTile({ label, read, total }) {
  const share = total > 0 ? read / total : null;
  return (
    <div className={`${card} px-4 py-[13px]`}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label">
        {label}
      </div>
      <div className="mt-[3px] text-[22px] font-bold leading-none text-cl-text">
        {num(read)}
      </div>
      <div className="mt-[4px] text-[11.5px] text-cl-meta">
        {share === null ? "—" : `${Math.round(share * 100)}% of ${num(total)} jobs`}
      </div>
    </div>
  );
}

function JobDialog({ refNum, onClose }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: null, data: null });
    getPaydJob(refNum)
      .then(
        (data) =>
          !cancelled &&
          // toPaydDetail returns null for anything that is not a record — an
          // empty body, an HTML login page — and rendering that as data threw.
          setState(
            data
              ? { loading: false, error: null, data }
              : {
                  loading: false,
                  error: "The PAYD portal did not return this job.",
                  data: null,
                },
          ),
      )
      .catch(
        (error) =>
          !cancelled &&
          setState({
            loading: false,
            error: error?.message || "Could not load this job",
            data: null,
          }),
      );
    return () => {
      cancelled = true;
    };
  }, [refNum]);

  useEffect(() => {
    const onKey = (event) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const job = state.data;

  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center bg-[rgba(19,23,32,0.45)] px-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`${card} max-h-[88vh] w-full max-w-[760px] overflow-hidden`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-cl-head px-5 py-4">
          <div>
            <h2 className="text-[15px] font-bold text-cl-text">Job {refNum}</h2>
            {job && (
              <p className="mt-[2px] text-[11.5px] text-cl-meta">
                {job.fields.filter((f) => f.value).length} of 3 read
                {job.closedBy ? ` · closed by ${job.closedBy}` : ""}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-tile p-1 text-cl-faint transition-colors hover:bg-cl-hover hover:text-cl-text"
          >
            <X className="h-[15px] w-[15px]" />
          </button>
        </div>

        <div className="cl-scroll max-h-[70vh] overflow-y-auto px-5 py-[18px]">
          {state.loading ? (
            <div className="flex items-center gap-3 py-10 text-[12.5px] text-cl-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading the photographs…
            </div>
          ) : state.error ? (
            <p className="py-8 text-center text-[12.5px] text-cl-muted">{state.error}</p>
          ) : !job ? null : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {job.fields.map((field) => (
                  <div key={field.key}>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label">
                      {field.label}
                    </div>
                    <div className="mt-[3px] text-[14px]">
                      <Reading value={field.value} />
                    </div>
                    {field.image ? (
                      <ZoomImage
                        src={paydImageUrl(field.image)}
                        alt={`${field.label} as photographed`}
                      />
                    ) : (
                      <p className="mt-2 rounded-tile border border-dashed border-cl-control px-3 py-6 text-center text-[11.5px] text-cl-faint">
                        no photograph
                      </p>
                    )}
                    {field.review && (
                      <p className="mt-2 text-[11.5px] text-cl-muted">
                        Reviewed as {field.review}
                        {field.reviewedBy ? ` by ${field.reviewedBy}` : ""}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              {job.remarks && (
                <p className="mt-4 rounded-tile border border-cl-card bg-cl-bg px-3 py-[9px] text-[12px] text-cl-secondary">
                  {job.remarks}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const defaultRange = () => {
  const to = new Date();
  const from = new Date(to.getTime() - RANGE_DAYS * 86400000);
  return { from: iso(from), to: iso(to) };
};

export default function PaydPage() {
  // `page` is which page the NEXT request asks for; `rows` accumulates every
  // page fetched so far for the current filters — state.data still holds
  // only the latest response, since total/totalPages/read are whole-dataset
  // figures that don't need accumulating, only the rows do.
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [loadingMore, setLoadingMore] = useState(false);
  // Separate from state.error on purpose: a failed "load more" must not
  // replace the rows already on screen with the full-page error view —
  // state.error does exactly that (see the render below), so an append
  // failure gets its own small, inline, retryable notice instead.
  const [loadMoreError, setLoadMoreError] = useState(null);
  const [openRef, setOpenRef] = useState(null);
  const scrollRef = useRef(null);

  // Status and date range are both real server-side filters (see
  // paydApi.js) — the date range scopes the NumberPlate/Odo/Vin read
  // counters too, not just the rows, so it has to be a request parameter
  // rather than a client-side slice of one page.
  const [status, setStatus] = useState("0");
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const statusDropdownRef = useRef(null);

  // Applied range drives the request; draft is what the picker's inputs show
  // while being edited, so dragging through the calendar doesn't fire a
  // request per click — only "Apply" commits it.
  const [appliedRange, setAppliedRange] = useState(defaultRange);
  const [draftRange, setDraftRange] = useState(defaultRange);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef(null);

  useEffect(() => {
    if (!showStatusDropdown) return undefined;
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showStatusDropdown]);

  useEffect(() => {
    if (!showDatePicker) return undefined;
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDatePicker]);

  // `append`: false for the first page of a fresh filter (replaces the list,
  // shows the full-page loading state), true for infinite scroll loading the
  // next one (keeps the existing rows on screen, shows the small footer
  // spinner instead).
  const load = useCallback(
    (targetPage, append) => {
      if (append) {
        setLoadingMore(true);
        setLoadMoreError(null);
      } else {
        setState((s) => ({ ...s, loading: true, error: null }));
        setLoadMoreError(null);
      }

      getPaydJobs(targetPage, {
        status,
        startDate: appliedRange.from,
        endDate: appliedRange.to,
      })
        .then((data) => {
          setState({ loading: false, error: null, data });
          setRows((prev) => (append ? [...prev, ...data.rows] : data.rows));
          setPage(targetPage);
        })
        .catch((error) => {
          const message =
            error?.response?.status === 401 || error?.response?.status === 403
              ? "The PAYD portal did not accept this session."
              : error?.message || "Could not load PAYD jobs";
          // An append failure leaves the rows already on screen in place —
          // only a fresh (non-append) failure blanks the list — so a hiccup
          // loading page 4 doesn't throw away pages 1 through 3.
          if (append) setLoadMoreError(message);
          else setState({ loading: false, error: message, data: null });
        })
        .finally(() => {
          if (append) setLoadingMore(false);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status, appliedRange.from, appliedRange.to],
  );

  useEffect(() => {
    load(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const hasMore = Boolean(state.data && page < state.data.totalPages);

  const loadMore = useCallback(() => {
    if (loadingMore || state.loading || !hasMore) return;
    load(page + 1, true);
  }, [loadingMore, state.loading, hasMore, page, load]);

  const sentinelRef = useInfiniteScrollSentinel({
    rootRef: scrollRef,
    enabled: hasMore,
    onLoadMore: loadMore,
  });

  const handleStatusSelect = (value) => {
    setStatus(value);
    setShowStatusDropdown(false);
  };

  const handleApplyRange = () => {
    setAppliedRange(draftRange);
    setShowDatePicker(false);
  };

  const setRangeShortcut = (days) => {
    const to = new Date();
    const from = new Date(to.getTime() - days * 86400000);
    setDraftRange({ from: iso(from), to: iso(to) });
  };

  const todayIso = iso(new Date());

  const jobs = state.data;
  const statusLabel =
    STATUS_OPTIONS.find((option) => option.value === status)?.label || "All statuses";

  return (
    <div className="cl-scroll h-full overflow-y-auto px-4 sm:px-8 pb-10 pt-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold tracking-[-0.01em] text-cl-text">PAYD</h1>
          <p className="mt-[2px] text-[12.5px] text-cl-muted">
            Pay-as-you-drive · plate, odometer and VIN read from photographs
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative" ref={statusDropdownRef}>
            <button
              type="button"
              onClick={() => setShowStatusDropdown((shown) => !shown)}
              className="flex items-center gap-[7px] rounded-control border border-cl-control bg-cl-surface px-3 py-[9px] text-[12.5px] font-medium text-cl-secondary transition-colors hover:border-cl-faint"
            >
              <span>{statusLabel}</span>
              <ChevronDown className="h-[10px] w-[10px] text-cl-faint" />
            </button>
            {showStatusDropdown && (
              <div className="absolute right-0 top-[calc(100%+6px)] z-dropdown min-w-[180px] rounded-tile border border-cl-card bg-cl-surface py-1 shadow-[0_8px_24px_rgba(19,23,32,0.10)]">
                <button
                  type="button"
                  onClick={() => handleStatusSelect("")}
                  className="block w-full px-3 py-2 text-left text-[12.5px] text-cl-secondary transition-colors hover:bg-cl-tint hover:text-cl-accent"
                >
                  All statuses
                </button>
                {STATUS_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleStatusSelect(option.value)}
                    className="block w-full px-3 py-2 text-left text-[12.5px] text-cl-secondary transition-colors hover:bg-cl-tint hover:text-cl-accent"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative" ref={datePickerRef}>
            <button
              type="button"
              onClick={() => setShowDatePicker((shown) => !shown)}
              className="flex items-center gap-[7px] rounded-control border border-cl-control bg-cl-surface px-3 py-[9px] text-[12.5px] font-medium text-cl-secondary transition-colors hover:border-cl-faint"
            >
              <span>
                {appliedRange.from} → {appliedRange.to}
              </span>
              <ChevronDown className="h-[10px] w-[10px] text-cl-faint" />
            </button>
            {showDatePicker && (
              <div className="absolute right-0 top-[calc(100%+6px)] z-dropdown w-[300px] rounded-tile border border-cl-card bg-cl-surface p-4 shadow-[0_8px_24px_rgba(19,23,32,0.10)]">
                <div className="mb-3 text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label">
                  Quick select
                </div>
                <div className="mb-4 flex gap-2">
                  {[
                    { days: 1, label: "Last day" },
                    { days: 7, label: "Last 7 days" },
                    { days: 30, label: "Last 30 days" },
                  ].map((shortcut) => (
                    <button
                      key={shortcut.days}
                      type="button"
                      onClick={() => setRangeShortcut(shortcut.days)}
                      className="flex-1 rounded-tile border border-cl-control bg-cl-surface px-2 py-[6px] text-[11px] font-medium text-cl-secondary transition-colors hover:border-cl-accent hover:text-cl-accent"
                    >
                      {shortcut.label}
                    </button>
                  ))}
                </div>

                <label className="mb-1 block text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label">
                  From
                </label>
                <input
                  type="date"
                  value={draftRange.from}
                  max={todayIso}
                  onChange={(event) =>
                    setDraftRange((current) => ({ ...current, from: event.target.value }))
                  }
                  className="cl-date-input mb-3 w-full rounded-tile border border-cl-control bg-cl-surface px-3 py-2 text-[12.5px] text-cl-text outline-none focus:border-cl-accent"
                />

                <label className="mb-1 block text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label">
                  To
                </label>
                <input
                  type="date"
                  value={draftRange.to}
                  min={draftRange.from}
                  max={todayIso}
                  onChange={(event) =>
                    setDraftRange((current) => ({ ...current, to: event.target.value }))
                  }
                  className="cl-date-input mb-4 w-full rounded-tile border border-cl-control bg-cl-surface px-3 py-2 text-[12.5px] text-cl-text outline-none focus:border-cl-accent"
                />

                <button
                  type="button"
                  onClick={handleApplyRange}
                  className="w-full rounded-tile bg-cl-accent px-3 py-2 text-[12px] font-semibold text-white shadow-cl-primary transition-colors hover:bg-cl-accent-hover"
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {jobs && (
        <div className="mt-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div className={`${card} px-4 py-[13px]`}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label">
              Jobs
            </div>
            <div className="mt-[3px] text-[22px] font-bold leading-none text-cl-text">
              {num(jobs.total)}
            </div>
            <div className="mt-[4px] text-[11.5px] text-cl-meta">
              {num(jobs.totalPages)} pages
            </div>
          </div>
          <ReadTile label="Plates read" read={jobs.read.plate} total={jobs.total} />
          <ReadTile label="Odometers read" read={jobs.read.odometer} total={jobs.total} />
          <ReadTile label="VINs read" read={jobs.read.vin} total={jobs.total} />
        </div>
      )}

      <div className={`${card} mt-4`}>
        <div className="flex items-baseline justify-between gap-3 border-b border-cl-head px-4 py-[11px]">
          <span className="text-[13.5px] font-semibold text-cl-text">Jobs</span>
          <span className="text-[11px] text-cl-meta">
            {jobs ? `${num(rows.length)} of ${num(jobs.total)} loaded` : ""}
          </span>
        </div>

        {state.loading ? (
          <div className="flex items-center gap-3 px-4 py-12 text-[12.5px] text-cl-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading jobs…
          </div>
        ) : state.error ? (
          <div className="px-4 py-10 text-center">
            <AlertTriangle
              className="mx-auto h-[15px] w-[15px]"
              style={{ color: "var(--cl-warn-chip-fg)" }}
            />
            <p className="mt-2 text-[12.5px] text-cl-muted">{state.error}</p>
            <button
              type="button"
              onClick={() => load(1, false)}
              className="mt-3 rounded-control border border-cl-control px-[13px] py-[6px] text-[12px] font-semibold text-cl-secondary transition-colors hover:bg-cl-hover"
            >
              Try again
            </button>
          </div>
        ) : rows.length === 0 ? (
          <p className="px-4 py-12 text-center text-[12.5px] text-cl-muted">
            No jobs match the selected status and date range.
          </p>
        ) : (
          <>
            <div ref={scrollRef} className="cl-scroll max-h-[560px] overflow-auto">
              <table className="w-full min-w-[720px] border-collapse text-[12.5px]">
                <thead>
                  <tr>
                    {["Reference", "Number plate", "Odometer", "VIN", "Review", "Captured", ""].map(
                      (h, i) => (
                        <th key={h || i} scope="col" className={headCell}>
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      className="cursor-pointer border-t border-cl-row transition-colors hover:bg-cl-hover"
                      onClick={() => setOpenRef(row.refNum)}
                    >
                      <td className={`${cell} font-medium text-cl-text`}>{row.refNum}</td>
                      <td className={cell}>
                        <Reading value={row.plate} />
                      </td>
                      <td className={cell}>
                        <Reading value={row.odometer} />
                      </td>
                      <td className={cell}>
                        <Reading value={row.vin} />
                      </td>
                      <td className={`${cell} text-cl-secondary`}>
                        {row.reviewStatus || "—"}
                      </td>
                      <td className={`${cell} whitespace-nowrap text-cl-muted`}>
                        {stamp(row.createdAt)}
                      </td>
                      <td className={`${cell} text-right`}>
                        <span
                          className="inline-flex items-center gap-[5px] text-[11.5px] font-semibold"
                          style={{ color: FILL }}
                        >
                          <ScanLine className="h-[12px] w-[12px]" />
                          Photos
                        </span>
                      </td>
                    </tr>
                  ))}
                  {/* Always rendered while there's more to load — not just
                      while actually loading — because it IS the trigger: the
                      observer below watches this row, so it has to already be
                      on screen (inside scrollRef) before the reader scrolls
                      near it, not only appear once loading has already
                      started. */}
                  <tr ref={sentinelRef}>
                    <td colSpan={7} className="px-3 py-[14px] text-center text-[11.5px] text-cl-meta">
                      {loadingMore ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Loading more…
                        </span>
                      ) : loadMoreError ? (
                        <span className="inline-flex items-center gap-2">
                          {loadMoreError}
                          <button
                            type="button"
                            onClick={() => load(page + 1, true)}
                            className="font-semibold text-cl-accent transition-colors hover:text-cl-accent-hover"
                          >
                            Retry
                          </button>
                        </span>
                      ) : !hasMore ? (
                        "You've reached the end of the list"
                      ) : null}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {openRef && <JobDialog refNum={openRef} onClose={() => setOpenRef(null)} />}
    </div>
  );
}
